import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';

import { Card, Container, Heading, InternalLink, animation, colors, media } from '../ui-components';
import renderRichText from '../utils/render-rich-text';
import { Link } from 'gatsby';
import { Text } from '../ui-components/components/text';

/**
 * Component that renders an offering card
 *
 * @component OfferingCard
 * 
 * @param {object} props
 * 
 * @prop {string} title
 * @prop {string} iconURL
 * @prop {object} content
 * @prop {string} layout
 * @prop {string} slug
 * @prop {string} partnerLogo
 * @prop {string} geoSpace
 * @prop {string} linkText
 * 
 * @returns {JSX}
 */
const OfferingCard = props => {
  const {
    title,
    iconURL,
    content,
    layout = 'withIcon',
    slug,
    partnerLogo,
    geoSpace,
    linkText = 'Explore Further'
  } = props;
  const imgAlt = `${title} Icon`;

  let contentText =
    content && typeof content === 'object' ? (
      renderRichText(content, geoSpace, 'medium')
    ) : (
      <Text size={'medium'}>{content}</Text>
    );

  let geoSpaceSlug = geoSpace && slug ? `/${geoSpace}${slug}` : slug ? slug : null;

  return geoSpaceSlug ? (
    <WrapperLink to={geoSpaceSlug}>
      <CardContainer className={layout} slug={slug}>
        <CardContent>
          {layout === 'withIcon' && <OfferingIcon src={iconURL} alt={imgAlt} />}
          <Heading level={5}>{title}</Heading>
          <OfferingSubtitleContainer className={layout}>{contentText}</OfferingSubtitleContainer>
        </CardContent>
        {layout === 'partnership' ? (
          <LinkAndLogo>
            {slug && (
              <OfferingLink to={slug} withArrow className={layout}>
                {linkText}
              </OfferingLink>
            )}
            <LogoWrapper>{partnerLogo && <Logo image={partnerLogo} alt={`${title} logo`} />}</LogoWrapper>
          </LinkAndLogo>
        ) : (
          slug && (
            <OfferingLink to={slug} withArrow className={layout}>
              {linkText}
            </OfferingLink>
          )
        )}
      </CardContainer>
    </WrapperLink>
  ) : (
    <WrapperContainer>
      <CardContainer className={layout} slug={slug}>
        <CardContent>
          {layout === 'withIcon' && <OfferingIcon src={iconURL} alt={imgAlt} />}
          <Heading level={5}>{title}</Heading>
          <OfferingSubtitleContainer className={layout}>{contentText}</OfferingSubtitleContainer>
        </CardContent>
        {layout === 'partnership' ? (
          <LinkAndLogo>
            {slug && (
              <OfferingLink to={slug} withArrow className={layout}>
                {linkText}
              </OfferingLink>
            )}
            <LogoWrapper>{partnerLogo && <Logo image={partnerLogo} alt={`${title} logo`} />}</LogoWrapper>
          </LinkAndLogo>
        ) : (
          slug && (
            <OfferingLink to={slug} withArrow className={layout}>
              {linkText}
            </OfferingLink>
          )
        )}
      </CardContainer>
    </WrapperContainer>
  );
};

export default OfferingCard;

const OfferingLink = styled(InternalLink)`
  &:hover {
    text-decoration: underline;

    a,
    svg {
      color: ${colors.linkHover};
    }
  }

  &:hover.noIcon,
  &:hover.wide {
    svg {
      color: ${colors.pureWhite};
    }
  }
`;

const WrapperLink = styled(props => <Link {...props} />)`
  color: ${colors.midnightBlack};
  display: flex;
  flex: 1;
  text-decoration: none;

  &:hover {
    div.partnership,
    div.wide,
    div.noIcon,
    div.withIcon,
    div.withBorderTop {
      ${OfferingLink}, a, svg {
        text-decoration: underline;
      }
    }

    div.partnership {
      background-color: ${colors.pureWhite};

      h5,
      p {
        color: ${colors.midnightBlack};
      }

      ${OfferingLink}, a, svg {
        color: ${colors.linkHover};
      }
    }

    div.withIcon,
    div.withBorderTop {
      background-color: ${colors.pureWhite};

      h5,
      p {
        color: ${colors.midnightBlack};
      }

      ${OfferingLink}, a, svg {
        color: ${colors.linkDefault};
      }
    }

    svg.rightArrow {
      transition: color ${animation.general.durationAndTiming}, transform ${animation.general.durationAndTiming};
      transform: translateX(2px);
    }
  }
`;

const WrapperContainer = styled(props => <div {...props} />)`
  color: ${colors.midnightBlack};
  display: flex;
  flex: 1;
  text-decoration: none;
`;

const CardContainer = styled(Card)`
  box-shadow: 0px 0px 80px 0px rgba(0, 0, 0, 0.06);
  justify-content: space-between;
  gap: 40px;
  width: 100%;
  min-height: 400px;
  padding: 40px;

  &.wide {
    max-width: 100%;
    min-height: 266px;

    @media screen and (max-width: ${media.largeBottom}) {
      min-height: 400px;
    }
  }

  &.withBorderTop {
    border-top: 6px solid ${colors.crederaRed};
    padding: 32px 28px;

    @media screen and (max-width: ${media.largeBottom}) {
      min-height: 180px;
    }
  }

  &:hover {
    background-color: ${({ slug }) => (slug ? colors.cardBackgroundHover : colors.pureWhite)};

    h5,
    p,
    a,
    svg {
      color: ${({ slug }) => (slug ? colors.pureWhite : colors.midnightBlack)};
    }
  }

  @media screen and (max-width: ${media.largeBottom}) {
    padding: 32px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0px 0px 40px;
`;

const OfferingIcon = styled.img`
  margin: 0;
  width: 80px;

  @media screen and (max-width: ${media.largeBottom}) {
    width: 68px;
  }
`;

const OfferingSubtitleContainer = styled(Container)`
  font-family: Lato, sans-serif;
  margin-top: 0;
  margin-left: 0;
  padding: 0;

  p {
    margin: 0;
  }
`;

const LinkAndLogo = styled.div`
  align-items: end;
  display: flex;
  justify-content: space-between;
`;

const LogoWrapper = styled.div`
  height: 40px;
  width: 100px;
  display: inline-flex;
  align-items: flex-end;
  justify-content: flex-end;

  @media screen and (max-width: ${media.largeBottom}) {
    display: none;
  }
`;

const Logo = styled(GatsbyImage)`
  img {
    max-height: 40px;
    max-width: 100px;
  }
`;
